import React, { useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { ISiteSettingsForm } from "../../types/forms";
import { updateSettings } from "../../lib/api";
import { alertActions } from "../../features/alert/alertSlice";
import TinyEditor from "../../components/Editor";
import { selectSetting } from "../../features/global/globalSlice";

const ResumeSettings = (): React.JSX.Element | undefined => {
    const dispatch = useAppDispatch();

    const settings = useAppSelector(state => state.global.settings);
    const resumeText = useAppSelector(state =>
        selectSetting(state, "majorybl_resume_text"),
    );
    const resumeQuestions = useAppSelector(state =>
        selectSetting(state, "majorybl_resume_questions"),
    );

    const [editorValue, setEditorValue] = useState<string>(resumeText?.setting);
    const settingsToShow = ["majorybl_resume_text"];

    const methods = useForm<Partial<ISiteSettingsForm>>({
        mode: "onChange",
        defaultValues: {
            resume: {
                ...settingsToShow.reduce(
                    (acc, settingName) => {
                        const setting = settings.find(
                            s => s.settingName === settingName,
                        );
                        if (setting) {
                            acc[settingName] = setting.setting;
                        }
                        return acc;
                    },
                    {} as Record<string, any>,
                ),
                ...settings
                    .find(s => s.settingName === "majorybl_resume_questions")
                    ?.setting.reduce(
                        (acc: Record<string, any>, s: any) => {
                            acc[`resume_question_${s.id}`] = s.question;
                            return acc;
                        },
                        {} as Record<string, any>,
                    ),
            },
        },
    });

    const watchResumeText = methods.watch(
        `resume.majorybl_resume_text`,
        methods.getValues(`resume.majorybl_resume_text`),
    );

    const onSubmit: SubmitHandler<Partial<ISiteSettingsForm>> = async data => {
        const dirtyFields = methods.formState.dirtyFields;

        const filteredData: Partial<ISiteSettingsForm> = {
            resume: {},
        };

        (Object.keys(dirtyFields) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                const dirtySubfields = dirtyFields[category];
                Object.keys(dirtySubfields).forEach(subfield => {
                    if (data[category]?.hasOwnProperty(subfield)) {
                        filteredData[category][subfield] =
                            data[category][subfield];
                    }
                });
            },
        );

        (Object.keys(filteredData) as Array<keyof ISiteSettingsForm>).forEach(
            category => {
                if (Object.keys(filteredData[category]).length === 0) {
                    delete filteredData[category];
                }
            },
        );

        await updateSettings(filteredData)
            .then(() => {
                dispatch(
                    alertActions.success({
                        title: "Settings updated successfully",
                        dismissible: true,
                    }),
                );

                methods.reset(data);
            })
            .catch(e =>
                dispatch(
                    alertActions.error({
                        title: "The server encountered a problem updating the settings.",
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                ),
            );
    };

    return (
        <section className="siteSettingsResumeContent">
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
                <Row>
                    <Col>
                        <h4>{resumeText?.displayTitle}</h4>
                        <Form.Control
                            type="hidden"
                            {...methods.register(
                                `resume.majorybl_resume_text`,
                                {
                                    value: watchResumeText
                                        ? "true"
                                        : methods.getValues(
                                              `resume.majorybl_resume_text`,
                                          ) || resumeText,
                                },
                            )}
                        />
                        <TinyEditor
                            editorValue={editorValue}
                            showSave={false}
                            formField={"resume.majorybl_resume_text"}
                            formMethods={methods}
                            setEditorValue={setEditorValue}
                        />
                    </Col>
                </Row>
                {resumeQuestions?.setting.map(
                    (q: { id: number; question: string }) => {
                        const settingName =
                            `resume.resume_question_${q.id}` as `resume.${string}`;
                        methods.watch(
                            settingName,
                            methods.getValues(settingName),
                        );

                        return (
                            <Row key={q.id}>
                                <Form.Group controlId={settingName} key={q.id}>
                                    <Form.Label>
                                        Resume Question #{q.id}
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...methods.register(settingName)}
                                        onChange={e => {
                                            methods.setValue(
                                                settingName,
                                                e.target.value.toString(),
                                                { shouldDirty: true },
                                            );
                                        }}
                                    />
                                </Form.Group>
                            </Row>
                        );
                    },
                )}
                <Button variant="primary" type="submit">
                    Update Settings
                </Button>
            </Form>
        </section>
    );
};

export default ResumeSettings;
